import React, { useState, useEffect, useRef } from "react";
import "./WhatWeDo.scss";

const INTERVAL = 4000; // ms per slide

const ConsultIcon = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <path d="M12 16v-4" />
    <path d="M12 8h.01" />
  </svg>
);

const AuditIcon = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
    <polyline points="10 9 9 9 8 9" />
  </svg>
);

const BuildIcon = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polygon points="12 2 2 7 12 12 22 7 12 2" />
    <polyline points="2 17 12 22 22 17" />
    <polyline points="2 12 12 17 22 12" />
  </svg>
);

const ScaleIcon = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
    <polyline points="17 6 23 6 23 12" />
  </svg>
);

const WhatWeDoArrowIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 8h10M9 4l4 4-4 4" />
  </svg>
);

const services = [
  {
    id: "consult",
    icon: <ConsultIcon />,
    title: "Consult",
    audience: "AI Transformation Strategy",
    description:
      "For leadership teams that need clarity before they commit budget, restructure platforms or scale operations. We help define the technology direction, AI opportunity, architecture priorities and commercial roadmap needed to move forward with confidence.",
    tagline: "No ambiguity. No open-ended retainers. Clarity, fast.",
    items: [
      "AI Transformation Strategy",
      "CTO Advisory",
      "Enterprise Architecture Strategy",
      "Product & Platform Strategy",
    ],
    link: "/services/consult",
    linkLabel: "Explore Consult Services",
  },
  {
    id: "audit",
    icon: <AuditIcon />,
    title: "Audit",
    audience: "AI Readiness & Technology Audit",
    description:
      "For organisations that need an independent view of their architecture, risk posture, delivery bottlenecks or compliance readiness. We assess where you are today, identify what is holding you back, and define the most practical next steps.",
    tagline: "Not just findings. A clear path to resolution.",
    items: [
      "AI Readiness Assessment",
      "Technology Stack Audit",
      "Cloud Architecture Review",
      "Technical Debt Assessment",
    ],
    link: "/services/audit",
    linkLabel: "Explore Audit Services",
  },
  {
    id: "build",
    icon: <BuildIcon />,
    title: "Build",
    audience: "AI-Powered Platform Development",
    description:
      "For businesses building new digital products, modernising legacy systems or delivering AI-enabled platform capabilities. We design and engineer secure, scalable solutions with senior oversight from architecture through production.",
    tagline:
      "Faster delivery is not a trade-off. It is what good engineering looks like now.",
    items: [
      "AI Platform Development",
      "SaaS Platform Engineering",
      "Product Development",
      "Platform Modernisation",
    ],
    link: "/services/build",
    linkLabel: "Explore Build Services",
  },
  {
    id: "scale",
    icon: <ScaleIcon />,
    title: "Scale",
    audience: "Platform Evolution & Engineering Scale",
    description:
      "For organisations that have built their platform but need to scale, optimise and continuously evolve. We provide senior-led engineering capability, structured delivery pods and long-term platform evolution programmes to ensure systems remain scalable, secure and aligned to business growth.",
    tagline: "Built to last. Engineered to grow.",
    items: [
      "Product Delivery Pods",
      "Engineering Capability Setup",
      "Platform Evolution Programme",
    ],
    link: "/services",
    linkLabel: "Explore Scale Services",
  },
];

const WhatWeDo = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const pausedRef = useRef(false);
  const elapsedRef = useRef(0);
  const lastFrameRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    const tick = (now) => {
      if (!pausedRef.current) {
        const delta =
          lastFrameRef.current != null ? now - lastFrameRef.current : 0;
        elapsedRef.current = Math.min(elapsedRef.current + delta, INTERVAL);
        const pct = (elapsedRef.current / INTERVAL) * 100;
        setProgress(pct);
        if (elapsedRef.current >= INTERVAL) {
          elapsedRef.current = 0;
          setActiveIndex((prev) => (prev + 1) % services.length);
        }
      }
      lastFrameRef.current = now;
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  // Reset progress when tab changes externally (auto or click)
  const handleTabClick = (i) => {
    elapsedRef.current = 0;
    setProgress(0);
    setActiveIndex(i);
  };

  const handleMouseEnter = () => {
    pausedRef.current = true;
  };
  const handleMouseLeave = () => {
    pausedRef.current = false;
  };

  const active = services[activeIndex];

  return (
    <section className="svc">
      <div className="svc__container">
        {/* Header */}
        <div className="svc__header">
          <span className="svc__label">What We Do</span>
          <h2 className="svc__title">
            <span className="svc__title-consult">Consult.</span>{" "}
            <span className="svc__title-audit">Audit.</span>{" "}
            <span className="svc__title-build">Build.</span>{" "}
            <span className="svc__title-scale">Scale.</span>
            <br />
            <span className="svc__title-end">End to End.</span>
          </h2>
          <p className="svc__subtitle">
            digiRyte integrates strategy, assurance and delivery into one model
            - so decisions become roadmaps, roadmaps become action, and action
            becomes production-ready outcomes.
          </p>
        </div>

        {/* Tab bar */}
        <nav className="svc__tabs" aria-label="Services">
          {services.map((s, i) => (
            <button
              key={s.id}
              className={`svc__tab${
                activeIndex === i ? " svc__tab--active" : ""
              }`}
              onClick={() => handleTabClick(i)}
              aria-selected={activeIndex === i}
            >
              <span className="svc__tab-icon">{s.icon}</span>
              <span className="svc__tab-title">{s.title}</span>
              {/* Progress bar — only on active tab */}
              <span
                className="svc__tab-progress"
                style={
                  activeIndex === i
                    ? { width: `${progress}%` }
                    : { width: "0%" }
                }
              />
            </button>
          ))}
        </nav>

        {/* Content panel */}
        <div
          className={`svc__panel svc__panel--${active.id}`}
          key={active.id}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <div className="svc__panel-left">
            <div className="svc__panel-icon">{active.icon}</div>
            <p className="svc__panel-audience">{active.audience}</p>
            <p className="svc__panel-desc">{active.description}</p>
            <p className="svc__panel-tagline">{active.tagline}</p>
            <a href={active.link} className="svc__panel-link">
              {active.linkLabel} <WhatWeDoArrowIcon />
            </a>
          </div>

          <ul className="svc__panel-list">
            {active.items.map((item, j) => (
              <li key={j} style={{ animationDelay: `${j * 80}ms` }}>
                {item}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
};

export default WhatWeDo;
